import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { axiosClient } from '../../../core/axios-client'

const API = {
  list: (companyId) => `/companies/${companyId}/documents`,
  one: (companyId, id) => `/companies/${companyId}/documents/${id}`,
  supportedPlaceholders: (companyId) => `/companies/${companyId}/documents/supported-placeholders`,
  types: (companyId) => `/companies/${companyId}/documents/types`,
  docPlaceholders: (companyId, id) => `/companies/${companyId}/documents/${id}/placeholders`,
}

const normalizeListResponse = (data) => {
  if (Array.isArray(data)) return { count: data.length, results: data }
  if (data && typeof data === 'object') {
    if (Array.isArray(data.results)) return { count: data.count ?? data.results.length, results: data.results }
    if (Array.isArray(data.items)) return { count: data.count ?? data.items.length, results: data.items }
  }
  return { count: 0, results: [] }
}

const resolveCompanyId = (companyId, thunkAPI) => {
  if (companyId) return String(companyId)
  const state = thunkAPI.getState?.() || {}
  return String(state?.companies?.selected_company?.id || '')
}

export const fetchContractTemplates = createAsyncThunk(
  'contractTemplates/fetchContractTemplates',
  async ({ companyId, filters = {} } = {}, thunkAPI) => {
    const cid = resolveCompanyId(companyId, thunkAPI)
    if (!cid) return { count: 0, results: [] }

    const res = await axiosClient.get(API.list(cid), { params: { ...filters } })
    return normalizeListResponse(res.data)
  }
)

export const fetchSupportedPlaceholders = createAsyncThunk(
  'contractTemplates/fetchSupportedPlaceholders',
  async ({ companyId } = {}, thunkAPI) => {
    const cid = resolveCompanyId(companyId, thunkAPI)
    if (!cid) return { companyId: '', results: [] }

    const res = await axiosClient.get(API.supportedPlaceholders(cid))
    const arr = Array.isArray(res.data)
      ? res.data
      : (Array.isArray(res.data?.results) ? res.data.results : [])

    return { companyId: cid, results: arr }
  }
)

export const fetchDocumentTypes = createAsyncThunk(
  'contractTemplates/fetchDocumentTypes',
  async ({ companyId } = {}, thunkAPI) => {
    const cid = resolveCompanyId(companyId, thunkAPI)
    if (!cid) return { companyId: '', results: [] }

    const res = await axiosClient.get(API.types(cid))
    const arr = Array.isArray(res.data)
      ? res.data
      : (Array.isArray(res.data?.results) ? res.data.results : [])

    return { companyId: cid, results: arr }
  }
)

export const fetchContractTemplate = createAsyncThunk(
  'contractTemplates/fetchContractTemplate',
  async ({ companyId, id } = {}, thunkAPI) => {
    const cid = resolveCompanyId(companyId, thunkAPI)
    if (!cid || !id) throw new Error('companyId/id is required')

    const res = await axiosClient.get(API.one(cid, id))
    return res.data
  }
)

export const fetchContractTemplatePlaceholders = createAsyncThunk(
  'contractTemplates/fetchContractTemplatePlaceholders',
  async ({ companyId, id } = {}, thunkAPI) => {
    const cid = resolveCompanyId(companyId, thunkAPI)
    if (!cid || !id) return { companyId: cid || '', documentId: id ?? null, placeholders: [] }

    const res = await axiosClient.get(API.docPlaceholders(cid, id))
    const placeholders = Array.isArray(res.data?.placeholders) ? res.data.placeholders : []
    return { companyId: cid, documentId: Number(id), placeholders }
  }
)

export const createContractTemplate = createAsyncThunk(
  'contractTemplates/createContractTemplate',
  async ({ companyId, name, document_type_id, attachment_id, isDefault } = {}, thunkAPI) => {
    const cid = resolveCompanyId(companyId, thunkAPI)
    if (!cid) throw new Error('companyId is required')

    const res = await axiosClient.post(API.list(cid), {
      name,
      document_type_id,
      attachment_id,
      default: isDefault,
    })
    return res.data
  }
)

export const updateContractTemplate = createAsyncThunk(
  'contractTemplates/updateContractTemplate',
  async ({ companyId, id, name, document_type_id, attachment_id, isDefault } = {}, thunkAPI) => {
    const cid = resolveCompanyId(companyId, thunkAPI)
    if (!cid || !id) throw new Error('companyId/id is required')

    const res = await axiosClient.put(API.one(cid, id), {
      name,
      document_type_id,
      attachment_id,
      default: isDefault,
    })
    return res.data
  }
)

export const patchContractTemplate = createAsyncThunk(
  'contractTemplates/patchContractTemplate',
  async ({ companyId, id, patch = {} } = {}, thunkAPI) => {
    const cid = resolveCompanyId(companyId, thunkAPI)
    if (!cid || !id) throw new Error('companyId/id is required')

    const currentRes = await axiosClient.get(API.one(cid, id))
    const doc = currentRes.data || {}

    const currentName = doc?.name ?? ''
    const currentTypeId = doc?.document_type?.id ?? doc?.document_type_id ?? null
    const currentAttachmentId = doc?.attachment?.id ?? doc?.attachment_id ?? null
    const currentDefault = doc?.default ?? null

    let nextTypeId = patch.document_type_id ?? patch.documentTypeId ?? null

    if (!nextTypeId && patch.contract_type) {
      const state = thunkAPI.getState?.() || {}
      const types = state?.contractTemplates?.documentTypes?.items || []
      const found = types.find((t) => String(t?.code || '') === String(patch.contract_type))
      if (found?.id) nextTypeId = found.id
    }

    const next = {
      name: patch.name ?? currentName,
      document_type_id: nextTypeId ?? currentTypeId,
      attachment_id: patch.attachment_id ?? currentAttachmentId,
      default: patch.default ?? currentDefault,
    }

    const res = await axiosClient.put(API.one(cid, id), next)
    return res.data
  }
)

export const deleteContractTemplate = createAsyncThunk(
  'contractTemplates/deleteContractTemplate',
  async ({ companyId, id } = {}, thunkAPI) => {
    const cid = resolveCompanyId(companyId, thunkAPI)
    if (!cid || !id) throw new Error('companyId/id is required')

    const res = await axiosClient.delete(API.one(cid, id))
    return { id, data: res.data }
  }
)

const initialState = {
  listItems: {
    items: [],
    page: 1,
    page_size: 10,
    count: 0,
  },
  current: null,

  isListLoading: false,
  listError: null,

  isCurrentLoading: false,
  currentError: null,

  placeholders: {
    items: [],
    companyId: '',
    isLoading: false,
    error: null,
  },

  documentTypes: {
    items: [],
    companyId: '',
    isLoading: false,
    error: null,
  },

  currentPlaceholders: {
    items: [],
    companyId: '',
    documentId: null,
    isLoading: false,
    error: null,
  },
}

export const slice = createSlice({
  name: 'contractTemplates',
  initialState,
  reducers: {
    setListItems(state, { payload }) {
      state.listItems = { ...state.listItems, ...payload }
    },
    resetCurrent(state) {
      state.current = null
      state.currentError = null
      state.isCurrentLoading = false

      state.currentPlaceholders = {
        items: [],
        companyId: '',
        documentId: null,
        isLoading: false,
        error: null,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContractTemplates.pending, (state) => {
        state.isListLoading = true
        state.listError = null
      })
      .addCase(fetchContractTemplates.fulfilled, (state, { payload }) => {
        const { count = 0, results = [] } = payload || {}
        state.listItems = { ...state.listItems, items: results, count }
        state.isListLoading = false
      })
      .addCase(fetchContractTemplates.rejected, (state, action) => {
        state.isListLoading = false
        state.listError = action?.error?.message || 'Ошибка загрузки'
      })

      .addCase(fetchSupportedPlaceholders.pending, (state) => {
        state.placeholders.isLoading = true
        state.placeholders.error = null
      })
      .addCase(fetchSupportedPlaceholders.fulfilled, (state, { payload }) => {
        state.placeholders.isLoading = false
        state.placeholders.companyId = payload?.companyId || ''
        state.placeholders.items = payload?.results || []
      })
      .addCase(fetchSupportedPlaceholders.rejected, (state, action) => {
        state.placeholders.isLoading = false
        state.placeholders.error = action?.error?.message || 'Ошибка загрузки ключей'
      })

      .addCase(fetchDocumentTypes.pending, (state) => {
        state.documentTypes.isLoading = true
        state.documentTypes.error = null
      })
      .addCase(fetchDocumentTypes.fulfilled, (state, { payload }) => {
        state.documentTypes.isLoading = false
        state.documentTypes.companyId = payload?.companyId || ''
        state.documentTypes.items = payload?.results || []
      })
      .addCase(fetchDocumentTypes.rejected, (state, action) => {
        state.documentTypes.isLoading = false
        state.documentTypes.error = action?.error?.message || 'Ошибка загрузки типов'
      })

      .addCase(fetchContractTemplate.pending, (state) => {
        state.isCurrentLoading = true
        state.currentError = null
      })
      .addCase(fetchContractTemplate.fulfilled, (state, { payload }) => {
        state.current = payload
        state.isCurrentLoading = false
      })
      .addCase(fetchContractTemplate.rejected, (state, action) => {
        state.isCurrentLoading = false
        state.currentError = action?.error?.message || 'Ошибка загрузки'
      })

      .addCase(fetchContractTemplatePlaceholders.pending, (state) => {
        state.currentPlaceholders.isLoading = true
        state.currentPlaceholders.error = null
      })
      .addCase(fetchContractTemplatePlaceholders.fulfilled, (state, { payload }) => {
        state.currentPlaceholders.isLoading = false
        state.currentPlaceholders.companyId = payload?.companyId || ''
        state.currentPlaceholders.documentId = payload?.documentId ?? null
        state.currentPlaceholders.items = payload?.placeholders || []
      })
      .addCase(fetchContractTemplatePlaceholders.rejected, (state, action) => {
        state.currentPlaceholders.isLoading = false
        state.currentPlaceholders.error = action?.error?.message || 'Ошибка загрузки ключей документа'
      })

      .addCase(createContractTemplate.fulfilled, (state) => {
        // state.current = payload
      })
      .addCase(updateContractTemplate.fulfilled, (state, { payload }) => {
        state.current = payload
      })
      .addCase(patchContractTemplate.fulfilled, (state, { payload }) => {
        state.current = payload
      })
      .addCase(deleteContractTemplate.fulfilled, (state, { payload }) => {
        const id = payload?.id
        if (!id) return
        state.listItems.items = (state.listItems.items || []).filter((x) => x.id !== id)
        if (state.current?.id === id) state.current = null
      })
  },
})

export const { setListItems: setContractTemplatesListItems, resetCurrent } = slice.actions
export default slice.reducer